import { useState, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Link } from 'react-router-dom';
import type { Document } from '../../types';
import { documentsApi } from '../../api/client';

interface DocumentEditorProps {
  document: Document;
}

type SaveStatus = 'saved' | 'saving' | 'error';

const typeLabels: Record<string, string> = {
  master: 'Master Document',
  book: 'Book',
  strategy: 'Strategy',
  research: 'Research',
  presentation: 'Presentation',
  brand: 'Brand',
};

const statusColors: Record<string, string> = {
  draft: 'bg-mist-gray',
  review: 'bg-muted-gold',
  final: 'bg-seal-red',
};

export default function DocumentEditor({ document }: DocumentEditorProps) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('saved');
  const [wordCount, setWordCount] = useState(document.wordCount);
  const [lastSaved, setLastSaved] = useState<Date>(new Date(document.updatedAt));

  const editor = useEditor({
    extensions: [StarterKit],
    content: document.content,
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[500px]',
      },
    },
    onUpdate: ({ editor }) => {
      const text = editor.getText();
      const words = text.trim().split(/\s+/).filter(word => word.length > 0);
      setWordCount(words.length);
    },
  });

  const saveDocument = useCallback(async () => {
    if (!editor) return;

    setSaveStatus('saving');
    try {
      const content = editor.getHTML();
      await documentsApi.save(document.slug, content);
      setSaveStatus('saved');
      setLastSaved(new Date());
    } catch (error) {
      console.error('Failed to save document:', error);
      setSaveStatus('error');
    }
  }, [editor, document.slug]);

  useEffect(() => {
    if (!editor) return;

    const timeoutId = setTimeout(() => {
      if (saveStatus !== 'saving') {
        saveDocument();
      }
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [editor?.getHTML(), saveDocument, saveStatus]);

  const formatLastSaved = () => {
    const now = new Date();
    const diff = now.getTime() - lastSaved.getTime();
    const minutes = Math.floor(diff / 60000);
    
    if (minutes < 1) return 'Just now';
    if (minutes === 1) return '1 minute ago';
    if (minutes < 60) return `${minutes} minutes ago`;
    return lastSaved.toLocaleTimeString();
  };

  const getSaveStatusDisplay = () => {
    switch (saveStatus) {
      case 'saving':
        return (
          <span className="flex items-center gap-1 text-muted-gold">
            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            Saving...
          </span>
        );
      case 'saved':
        return (
          <span className="flex items-center gap-1 text-mountain-green">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Saved
          </span>
        );
      case 'error':
        return (
          <span className="flex items-center gap-1 text-seal-red">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Error saving
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-paper-bg">
      {/* Header */}
      <header className="bg-white border-b border-muted-gold sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 text-ink-black hover:text-seal-red transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="text-sm font-medium">Back to Dashboard</span>
            </Link>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-4 text-sm">
                <span className={`px-2 py-1 rounded text-white text-xs ${statusColors[document.status]}`}>
                  {document.status}
                </span>
                <span className="text-stone-gray">
                  {typeLabels[document.type] || document.type}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Document Title */}
        <div className="mb-8">
          <h1 className="font-serif text-4xl font-bold text-ink-black mb-4 leading-tight">
            {document.title}
          </h1>
          <div className="flex items-center gap-6 text-sm text-stone-gray">
            <span>{wordCount.toLocaleString()} words</span>
            <span>•</span>
            <span>Last edited {formatLastSaved()}</span>
            <span>•</span>
            {getSaveStatusDisplay()}
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-muted-gold rounded-t-sm p-3 flex items-center gap-2 flex-wrap">
          <button
            onClick={() => editor?.chain().focus().toggleBold().run()}
            disabled={!editor}
            className={`p-2 rounded transition-colors ${
              editor?.isActive('bold')
                ? 'bg-seal-red text-white'
                : 'hover:bg-aged-paper text-ink-black'
            }`}
            title="Bold"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 12h8a4 4 0 100-8H6v8zm0 0h10a4 4 0 110 8H6v-8z" />
            </svg>
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleItalic().run()}
            disabled={!editor}
            className={`p-2 rounded transition-colors ${
              editor?.isActive('italic')
                ? 'bg-seal-red text-white'
                : 'hover:bg-aged-paper text-ink-black'
            }`}
            title="Italic"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </button>
          <div className="w-px h-6 bg-muted-gold mx-2" />
          <button
            onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
            disabled={!editor}
            className={`p-2 rounded transition-colors font-serif ${
              editor?.isActive('heading', { level: 1 })
                ? 'bg-seal-red text-white'
                : 'hover:bg-aged-paper text-ink-black'
            }`}
            title="Heading 1"
          >
            H1
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
            disabled={!editor}
            className={`p-2 rounded transition-colors font-serif ${
              editor?.isActive('heading', { level: 2 })
                ? 'bg-seal-red text-white'
                : 'hover:bg-aged-paper text-ink-black'
            }`}
            title="Heading 2"
          >
            H2
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
            disabled={!editor}
            className={`p-2 rounded transition-colors font-serif ${
              editor?.isActive('heading', { level: 3 })
                ? 'bg-seal-red text-white'
                : 'hover:bg-aged-paper text-ink-black'
            }`}
            title="Heading 3"
          >
            H3
          </button>
          <div className="w-px h-6 bg-muted-gold mx-2" />
          <button
            onClick={() => editor?.chain().focus().toggleBulletList().run()}
            disabled={!editor}
            className={`p-2 rounded transition-colors ${
              editor?.isActive('bulletList')
                ? 'bg-seal-red text-white'
                : 'hover:bg-aged-paper text-ink-black'
            }`}
            title="Bullet List"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleOrderedList().run()}
            disabled={!editor}
            className={`p-2 rounded transition-colors ${
              editor?.isActive('orderedList')
                ? 'bg-seal-red text-white'
                : 'hover:bg-aged-paper text-ink-black'
            }`}
            title="Numbered List"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h12M7 12h12M7 17h12M3 7h.01M3 12h.01M3 17h.01" />
            </svg>
          </button>
          <button
            onClick={() => editor?.chain().focus().toggleBlockquote().run()}
            disabled={!editor}
            className={`p-2 rounded transition-colors ${
              editor?.isActive('blockquote')
                ? 'bg-seal-red text-white'
                : 'hover:bg-aged-paper text-ink-black'
            }`}
            title="Quote"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </button>
          <div className="flex-1" />
          <button
            onClick={() => editor?.chain().focus().undo().run()}
            disabled={!editor?.can().undo()}
            className="p-2 rounded hover:bg-aged-paper text-ink-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Undo"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          <button
            onClick={() => editor?.chain().focus().redo().run()}
            disabled={!editor?.can().redo()}
            className="p-2 rounded hover:bg-aged-paper text-ink-black disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Redo"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
        </div>

        {/* Editor */}
        <div className="bg-white border-x border-b border-muted-gold rounded-b-sm shadow-sm">
          <EditorContent 
            editor={editor} 
            className="p-8 font-sans text-ink-black leading-relaxed"
          />
        </div>

        {/* Footer */}
        <div className="mt-6 flex items-center justify-between text-sm text-stone-gray">
          <div className="flex items-center gap-4">
            <span>Created: {new Date(document.createdAt).toLocaleDateString()}</span>
            <span>•</span>
            <span>Updated: {new Date(document.updatedAt).toLocaleDateString()}</span>
          </div>
          <div className="flex items-center gap-4">
            <span>{wordCount.toLocaleString()} words</span>
            {getSaveStatusDisplay()}
          </div>
        </div>
      </main>
    </div>
  );
}
